/**
 * AtypicalValuesNote Component
 *
 * A small note shown beneath a routine surveillance scatter plot when the
 * plotted rows are uniform across the category columns but one or more of those
 * shared values is not a "typical"/default value (see computeAtypicalValues).
 * It renders an info icon + "Atypical values" text, with a tooltip listing each
 * atypical column and its value.
 *
 * Renders nothing when there are no atypical values, so callers can drop it
 * under every plot unconditionally.
 */

import { Info } from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { AtypicalValue } from "@/components/rs-data/rsPlotData"

/** Props for the AtypicalValuesNote component. */
interface AtypicalValuesNoteProps {
  /**
   * The atypical category values (label + value) for the plot's rows, from
   * computeAtypicalValues. When empty, the note renders nothing.
   */
  atypicalValues: AtypicalValue[]
}

/**
 * Note surfacing non-default category values shared by a plot's rows.
 *
 * @param props - Component props.
 * @param props.atypicalValues - The atypical column/value pairs. When empty,
 *        nothing is rendered.
 * @returns The note (icon + text + tooltip), or null when there is nothing
 *          atypical to report.
 */
export default function AtypicalValuesNote({
  atypicalValues,
}: AtypicalValuesNoteProps): React.JSX.Element | null {
  // Nothing atypical → no note. Lets callers render it unconditionally.
  if (atypicalValues.length === 0) {
    return null
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* A button so the tooltip is keyboard-focusable and accessible, not
            just hover-only. Styled as inline text, not a button. */}
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground cursor-help"
          aria-label="Uniform but atypical categories — details"
        >
          <Info className="size-3.5 shrink-0" aria-hidden="true" />
          Uniform but atypical categories
        </button>
      </TooltipTrigger>
      <TooltipContent>
        <p className="font-medium">Non-default category values:</p>
        <ul className="mt-1 list-disc pl-4">
          {atypicalValues.map(({ label, value }) => (
            <li key={label}>
              {label}: {value}
            </li>
          ))}
        </ul>
      </TooltipContent>
    </Tooltip>
  )
}
